import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useShowTagSelectorDropdown } from '@/hooks/useShowTagSelectorDropdown';
import { useViewingTagSelection } from '@/hooks/useViewingTagSelection';
import {
  useGetUserTagsQuery,
  useUpdateUserTagMutation,
  usersApi,
} from '@/store/usersApi';
import type { UserTag } from '@/store/userTag';
import type { AppDispatch } from '@/store/store';
import {
  diffAssignableUserTagMembership,
  mergeWealthAccountIds,
  removeWealthAccountIds,
  resolveAssignableUserTags,
  resolveAttributableViewingTag,
  resolveSelectedViewingTag,
} from '@/utils/viewingTagAccountAttribution';

/**
 * Exposes the current TagSelectorDropdown tag for Add Account prefill, and
 * attributes newly created/linked accounts onto assignable personal tags
 * (Assign or Edit on GET /api/user/tags).
 *
 * PUT /api/user/tags/{id} sends Name + WealthAccountIds only. Rights stay
 * server-owned so existing entitlements are not overwritten.
 *
 * Manual account create can send TagIds on POST. Edit cannot: the account PUT
 * updates the account record and does not change tag membership, so edit
 * follows the save with this hook. Yodlee links have no account body, so they
 * use the same PUT.
 *
 * Create and Yodlee attribution log and swallow a failed tag write so the
 * account or link still succeeds. Edit sync rejects instead, so the form can
 * stay open and the same membership change can be retried.
 */
export function useAttributeAccountsToViewingTag() {
  const dispatch = useDispatch<AppDispatch>();
  const selection = useViewingTagSelection();
  const authLoaded = useAuthIsLoaded();
  const showTagSelector = useShowTagSelectorDropdown();
  const { data: userTags } = useGetUserTagsQuery(undefined, {
    skip: !showTagSelector || !authLoaded,
  });
  const [updateUserTag] = useUpdateUserTagMutation();

  const selectedTag = resolveSelectedViewingTag(selection, userTags);
  const attributableTag = resolveAttributableViewingTag(selection, userTags);

  const loadTagAccountIds = useCallback(
    async (tagId: number): Promise<number[]> => {
      return dispatch(
        usersApi.endpoints.getUserTagAccounts.initiate(tagId, {
          forceRefetch: true,
          subscribe: false,
        }),
      ).unwrap();
    },
    [dispatch],
  );

  const putTagAccounts = useCallback(
    async (tag: UserTag, wealthAccountIds: number[]): Promise<void> => {
      // Do not send Rights: entitlements are server-owned on this endpoint.
      await updateUserTag({
        tagId: tag.tagId,
        name: tag.name,
        wealthAccountIds,
      }).unwrap();
    },
    [updateUserTag],
  );

  const addAccountsToTag = useCallback(
    async (tag: UserTag, uniqueNew: readonly number[]): Promise<void> => {
      const existing = await loadTagAccountIds(tag.tagId);
      const merged = mergeWealthAccountIds(existing, uniqueNew);
      if (
        merged.length === existing.length &&
        uniqueNew.every((id) => existing.includes(id))
      ) {
        return;
      }
      await putTagAccounts(tag, merged);
    },
    [loadTagAccountIds, putTagAccounts],
  );

  const attributeAccountsToTag = useCallback(
    async (tag: UserTag, uniqueNew: readonly number[]): Promise<void> => {
      try {
        await addAccountsToTag(tag, uniqueNew);
      } catch (error) {
        console.error(
          '[useAttributeAccountsToViewingTag] failed to attribute accounts',
          { tagId: tag.tagId, accountIds: uniqueNew, error },
        );
      }
    },
    [addAccountsToTag],
  );

  const dropAccountFromTag = useCallback(
    async (tag: UserTag, accountId: number): Promise<void> => {
      const existing = await loadTagAccountIds(tag.tagId);
      const next = removeWealthAccountIds(existing, [accountId]);
      if (next.length === existing.length) {
        return;
      }
      await putTagAccounts(tag, next);
    },
    [loadTagAccountIds, putTagAccounts],
  );

  /**
   * Adds `accountIds` to each assignable selected user tag. When `tagIds` is
   * omitted, attributes to the current header viewing tag only (Yodlee).
   */
  const attributeAccountIds = useCallback(
    async (
      accountIds: readonly number[],
      tagIds?: readonly number[],
    ): Promise<void> => {
      const viewingTag = resolveAttributableViewingTag(selection, userTags);
      const tagsToUpdate =
        tagIds != null
          ? resolveAssignableUserTags(tagIds, userTags)
          : viewingTag
            ? [viewingTag]
            : [];
      if (tagsToUpdate.length === 0) {
        return;
      }

      const uniqueNew = [
        ...new Set(
          accountIds.filter(
            (id): id is number => typeof id === 'number' && Number.isFinite(id),
          ),
        ),
      ];
      if (uniqueNew.length === 0) {
        return;
      }

      for (const tag of tagsToUpdate) {
        await attributeAccountsToTag(tag, uniqueNew);
      }
    },
    [attributeAccountsToTag, selection, userTags],
  );

  /**
   * Adds and removes `accountId` on assignable user tags so the tag-filtered
   * account list matches the account form. `TagIds` on the account record is
   * not enough for that list; membership is `PUT /api/user/tags/{id}`.
   * An empty next selection still removes the account from the previous tags.
   * Rejects when a tag-account read or write fails. Callers must not treat the
   * selection as saved until this resolves.
   */
  const syncAccountTagMembership = useCallback(
    async (
      accountId: number,
      previousTagIds: readonly number[],
      nextTagIds: readonly number[],
    ): Promise<void> => {
      if (!Number.isFinite(accountId)) {
        return;
      }
      const { added, removed } = diffAssignableUserTagMembership(
        previousTagIds,
        nextTagIds,
        userTags,
      );
      for (const tag of removed) {
        await dropAccountFromTag(tag, accountId);
      }
      for (const tag of added) {
        await addAccountsToTag(tag, [accountId]);
      }
    },
    [addAccountsToTag, dropAccountFromTag, userTags],
  );

  return {
    selectedTag,
    selectedTagId: selection?.tagId ?? null,
    attributableTag,
    attributableTagId: attributableTag?.tagId ?? null,
    attributeAccountIds,
    syncAccountTagMembership,
  };
}
